import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, X, Loader2, ArrowRight, Coins, GitBranch } from "lucide-react";
import axios from "axios";
import { getStoredEmail } from "../lib/useNewsezeUser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AiSearchBar() {
  const email = getStoredEmail();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("search"); // search | ask
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);  // search payload
  const [answer, setAnswer] = useState(null);    // ai answer payload
  const [credits, setCredits] = useState(0);
  const [errMsg, setErrMsg] = useState("");
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    setOpen(true);
    setResults(null);
    setAnswer(null);
    setErrMsg("");
    try {
      if (mode === "search") {
        const params = { q, limit: 8 };
        if (email) params.email = email;
        const r = await axios.get(`${API}/search`, { params, timeout: 8000 });
        setResults(r.data);
        setCredits((c) => c + (r.data.credits_awarded || 0));
      } else {
        const body = { q };
        if (email) body.email = email;
        const r = await axios.post(`${API}/ai/ask`, body, { timeout: 60000 });
        setAnswer(r.data);
        setCredits((c) => c + (r.data.credits_awarded || 0));
      }
    } catch (err) {
      console.error(err);
      setErrMsg(err?.response?.data?.detail || "Newseze couldn't reach the algorithm — try again.");
    } finally { setBusy(false); }
  };

  const placeholder = mode === "search"
    ? "Search trusted news — earn credits to look stuff up"
    : "Ask Newseze — answer cites the sources, you earn credits";

  return (
    <div ref={ref} className="relative flex-1 max-w-[640px] mx-3" data-testid="ai-search-bar">
      <form onSubmit={submit}>
        <div className="flex items-stretch bg-white border border-[var(--line)] rounded-full focus-within:border-[var(--blue)] overflow-hidden">
          <div className="flex items-center px-2 gap-1 border-r border-[var(--line)]">
            <button
              type="button"
              onClick={() => setMode("search")}
              className={`text-[10px] uppercase tracking-[0.16em] font-black px-2 py-1 rounded-full ${mode === "search" ? "bg-[var(--blue)] text-white" : "text-[var(--muted)]"}`}
              data-testid="ai-mode-search"
              aria-label="Switch to search"
            >
              <Search size={11} className="inline mr-0.5" /> Search
            </button>
            <button
              type="button"
              onClick={() => setMode("ask")}
              className={`text-[10px] uppercase tracking-[0.16em] font-black px-2 py-1 rounded-full ${mode === "ask" ? "bg-[var(--gold)] text-[#1d1303]" : "text-[var(--muted)]"}`}
              data-testid="ai-mode-ask"
              aria-label="Switch to Ask Newseze"
            >
              <Sparkles size={11} className="inline mr-0.5" /> Ask Newseze
            </button>
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results || answer ? setOpen(true) : null}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 text-[13.5px] outline-none bg-transparent"
            data-testid="ai-search-input"
          />
          <button
            type="submit"
            disabled={busy || q.trim().length < 2}
            className="px-3 bg-[var(--ink)] text-white disabled:opacity-50"
            aria-label="Run"
            data-testid="ai-search-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          </button>
        </div>
      </form>
      <div className="absolute right-1 -top-2 flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] font-black text-[var(--gold)] bg-[#1d1303] px-1.5 py-0.5 rounded">
        <Coins size={9} /> {mode === "search" ? "+5" : "+30"} credits / query
      </div>

      {/* Results dropdown */}
      {open && (results || answer || busy || errMsg) && (
        <div className="absolute left-0 right-0 mt-2 bg-white border border-[var(--line)] rounded-xl shadow-2xl max-h-[70vh] overflow-auto z-50" data-testid="ai-search-dropdown">
          <div className="sticky top-0 bg-white border-b border-[var(--line)] p-3 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--muted)]">
              {mode === "search" ? `Search · "${q}"` : `Ask Newseze · "${q}"`}
            </span>
            <div className="flex items-center gap-2">
              {credits > 0 && (
                <span className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--gold)] flex items-center gap-1">
                  <Coins size={10} /> +{credits} this session
                </span>
              )}
              <button onClick={() => setOpen(false)} aria-label="Close"><X size={14} className="text-[var(--muted)]" /></button>
            </div>
          </div>

          {busy && (
            <div className="p-6 flex items-center gap-2 justify-center text-[13px] text-[var(--muted)]">
              <Loader2 size={14} className="animate-spin" />
              {mode === "search" ? "Searching trust-ranked articles…" : "Newseze is reading the sources…"}
            </div>
          )}

          {!busy && errMsg && (
            <div className="p-4 text-[13px] text-[var(--red)] italic text-center" data-testid="ai-search-error">{errMsg}</div>
          )}

          {mode === "search" && results && !busy && (
            <div className="p-2" data-testid="ai-search-results">
              {results.results.length === 0 ? (
                <div className="text-[13px] text-[var(--muted)] italic p-4 text-center">No matches — try different keywords.</div>
              ) : results.results.map((r) => (
                <Link to={r.link} key={r.id} onClick={() => setOpen(false)}
                      className="block p-3 rounded-md hover:bg-[var(--bg)] border-b border-[var(--line)] last:border-0"
                      data-testid={`ai-search-result-${r.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">{r.category} · trust {r.trust}</div>
                  <div className="text-[14px] font-black text-[var(--ink)] mt-0.5">{r.title}</div>
                  <div className="text-[12px] text-[var(--muted)] mt-1">{r.source} · {r.summary?.slice(0, 140)}…</div>
                </Link>
              ))}
            </div>
          )}

          {mode === "ask" && answer && !busy && (
            <div className="p-4" data-testid="ai-ask-answer">
              <div className="bg-[var(--sky)] rounded-md p-3 mb-3">
                <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--blue)] mb-1 flex items-center gap-1">
                  <Sparkles size={11} /> Newseze answer
                </div>
                <p className="text-[13.5px] leading-snug text-[var(--ink)] whitespace-pre-wrap">{answer.answer}</p>
              </div>

              {answer.conflict && answer.conflict.camps?.length > 0 && (
                <ConflictBrief conflict={answer.conflict} />
              )}

              <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--muted)] mb-2">Cited sources</div>
              <div className="space-y-1">
                {answer.sources.map((s, i) => (
                  <Link to={s.link} key={s.id} onClick={() => setOpen(false)}
                        className="flex items-start gap-2 p-2 rounded-md hover:bg-[var(--bg)]"
                        data-testid={`ai-source-${i + 1}`}>
                    <span className="text-[10px] font-black text-[var(--blue)] bg-[var(--sky)] rounded px-1.5 py-0.5">[{i + 1}]</span>
                    <span className="text-[12.5px] flex-1">
                      <span className="font-bold text-[var(--ink)]">{s.title}</span>
                      <span className="text-[var(--muted)]"> — {s.source} · {s.category}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConflictBrief({ conflict }) {
  const conf = conflict.confidence ?? 0;
  // confidence = % of sources agreeing on core fact
  // High = consensus (green), Low = divergence (gold/red)
  const tone = conf >= 70 ? "green" : conf >= 40 ? "gold" : "red";
  const toneStyles = {
    green: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", badge: "bg-emerald-600" },
    gold: { bg: "bg-[#fff7d6]", border: "border-[var(--gold)]", text: "text-[#7a5b00]", badge: "bg-[var(--gold)] text-[#1d1303]" },
    red: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", badge: "bg-rose-600" },
  }[tone];
  const leanColor = (lean) => {
    const l = (lean || "").toLowerCase();
    if (l.includes("right")) return "text-rose-700 bg-rose-100";
    if (l.includes("left")) return "text-sky-700 bg-sky-100";
    if (l.includes("international")) return "text-violet-700 bg-violet-100";
    if (l.includes("voices")) return "text-amber-700 bg-amber-100";
    return "text-slate-700 bg-slate-100";
  };

  return (
    <div className={`rounded-md border ${toneStyles.border} ${toneStyles.bg} p-3 mb-3`} data-testid="ai-conflict-brief">
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[10px] uppercase tracking-[0.16em] font-black ${toneStyles.text} flex items-center gap-1.5`}>
          <GitBranch size={11} /> Narrative conflict brief
        </div>
        <span className={`text-[10px] uppercase tracking-[0.14em] font-black ${toneStyles.badge} text-white rounded-full px-2 py-0.5`} data-testid="ai-conflict-confidence">
          {conf}% agreement{conf === 0 ? " · fully divergent" : ""}
        </span>
      </div>
      {conflict.conflict_summary && (
        <p className={`text-[12.5px] ${toneStyles.text} italic mb-2.5 leading-snug`} data-testid="ai-conflict-summary">
          {conflict.conflict_summary}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {conflict.camps.map((c, i) => (
          <div key={i} className="bg-white rounded-md border border-[var(--line)] p-2.5" data-testid={`ai-conflict-camp-${i + 1}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] font-black text-[var(--ink)] leading-tight">{c.name}</div>
              {/* framing-camp lean badge hidden in public UI per editorial directive */}
            </div>
            <p className="text-[11.5px] text-[var(--muted)] leading-snug mb-1.5">{c.framing}</p>
            {c.sources?.length > 0 && (
              <div className="text-[10px] text-[var(--muted)] uppercase tracking-[0.1em] font-bold">
                {c.sources.slice(0, 3).join(" · ")}
                {c.sources.length > 3 ? ` · +${c.sources.length - 3}` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
